import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import NetworkTopology from '../GridStatus/NetworkTopology';

const STAGE3_INTRUSION_PATH = [
  { cx: 80,  cy: 250 },
  { cx: 250, cy: 250 },
  { cx: 450, cy: 250 },
  { cx: 650, cy: 250 },
];

function HelpModal({ onClose }) {
  return (
    <AnimatePresence>
      <motion.div
        key="ur-help-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          key="ur-help-panel"
          initial={{ opacity: 0, scale: 0.95, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 8 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md mx-4 bg-gray-950 border border-amber-700/50 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden max-h-[75vh] flex flex-col"
        >
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-amber-900/50 bg-amber-950/40">
            <div className="flex items-center gap-2.5">
              <span className="text-amber-300 font-bold text-base">?</span>
              <span className="text-sm font-semibold text-amber-200 tracking-wide">How to Play — Ukraine Incident Replay</span>
            </div>
            <button onClick={onClose} className="text-slate-500 hover:text-slate-300 text-lg leading-none transition-colors">✕</button>
          </div>
          <div className="px-5 py-4 flex flex-col gap-4 text-sm text-slate-300 leading-relaxed overflow-y-auto flex-1">
            <p>
              Relive the 2015 Ukraine power grid cyberattack stage by stage. At each phase of the attack chain, you'll see what the attacker did — your job is to choose the correct defensive response that would have stopped or limited the damage.
            </p>
            <ol className="flex flex-col gap-3 list-none">
              {[
                ['Read the situation', "Each stage shows a Situation panel describing the attacker's action at that point in the kill chain — from initial spearphishing through to the blackout."],
                ['Choose a defender response', 'Select one of the response options that best describes what the grid operators should have done. Only one option is correct.'],
                ['Submit your response', 'Click "Submit Response" to lock in your answer and receive feedback explaining why the correct control works.'],
                ['First-attempt bonus', 'Getting it right on your first try earns a first-attempt bonus. If you choose wrong, you can retry the stage — but the bonus is lost.'],
                ['Progress through all stages', 'The timeline at the top shows all stages. Work through all of them to complete the replay and see your total score.'],
              ].map(([title, desc], i) => (
                <li key={i} className="flex gap-3">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-amber-900/80 border border-amber-600/60 text-amber-300 text-[11px] font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  <div>
                    <div className="font-semibold text-slate-100 mb-0.5">{title}</div>
                    <div className="text-slate-400 text-xs">{desc}</div>
                  </div>
                </li>
              ))}
            </ol>
            <div className="px-3 py-2.5 rounded-lg border border-amber-800/40 bg-amber-950/30 text-amber-300/90 text-xs">
              <span className="font-semibold">Historical context:</span> The 2015 attack on Ukrainian regional power companies was the first confirmed cyberattack to cause a blackout. Attackers used spearphishing, stolen VPN credentials, and destructive malware to take 230,000 customers offline.
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export function StageTimeline({ stages, currentStage, stageResults }) {
  return (
    <div className="flex items-start gap-0 mb-6">
      {stages.map((stage, idx) => {
        const result = stageResults[idx];
        const isCurrent = idx === currentStage && !result;
        const isCorrect = result?.correct;

        return (
          <React.Fragment key={stage.id}>
            <div className="flex flex-col items-center" style={{ minWidth: 0, flex: '0 0 auto', width: '4.5rem' }}>
              <motion.div
                initial={false}
                animate={{
                  backgroundColor: isCorrect ? '#16a34a' : isCurrent ? '#d97706' : '#334155',
                  borderColor: isCorrect ? '#4ade80' : isCurrent ? '#fbbf24' : '#475569',
                }}
                transition={{ duration: 0.4 }}
                className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 text-white flex-shrink-0"
              >
                {isCorrect ? '✓' : idx + 1}
              </motion.div>
              <span
                className="text-xs text-slate-400 mt-1 text-center leading-tight"
                style={{ wordBreak: 'break-word', whiteSpace: 'normal', maxWidth: '4rem' }}
              >
                {stage.phase}
              </span>
            </div>
            {idx < stages.length - 1 && (
              <motion.div
                initial={false}
                animate={{ backgroundColor: idx < currentStage || stageResults[idx]?.correct ? '#16a34a' : '#334155' }}
                transition={{ duration: 0.4 }}
                className="flex-1 h-0.5 mx-1"
                style={{ marginTop: '1.125rem' }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function UkraineReplay({ step, onComplete, onScoreUpdate, onWrongAnswer, isReplay }) {
  const stages = step?.stages || [];
  const scorePerStage = step?.scorePerStage ?? 5;
  const firstAttemptBonus = step?.firstAttemptBonus ?? 3;

  const [currentStage, setCurrentStage] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [wrongAttempted, setWrongAttempted] = useState({});
  const [stageResults, setStageResults] = useState([]);
  const [totalScore, setTotalScore] = useState(0);
  const [showIntrusion, setShowIntrusion] = useState(false);
  const [nodeStates, setNodeStates] = useState({
    external: 'green', corporate: 'green', dmz: 'green', ot: 'green', field: 'green',
  });
  const [done, setDone] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const stage = stages[currentStage];
  const selectedOpt = stage?.options.find(o => o.id === selectedId);
  const isFirstAttempt = !wrongAttempted[currentStage];

  const handleSubmit = () => {
    if (!selectedOpt) return;
    setSubmitted(true);

    if (!selectedOpt.isCorrect) {
      setWrongAttempted(prev => ({ ...prev, [currentStage]: true }));
      if (currentStage === 2) {
        setShowIntrusion(true);
        setNodeStates(prev => ({ ...prev, dmz: 'amber', ot: 'red' }));
        onWrongAnswer && onWrongAnswer(currentStage);
      }
    } else {
      const pts = scorePerStage + (isFirstAttempt ? firstAttemptBonus : 0);
      setTotalScore(prev => prev + pts);
      setStageResults(prev => [
        ...prev,
        { correct: true, firstAttempt: isFirstAttempt, score: pts, phase: stage.phase },
      ]);
    }
  };

  const handleRetry = () => {
    setSelectedId(null);
    setSubmitted(false);
    setShowIntrusion(false);
  };

  const handleNext = () => {
    if (currentStage < stages.length - 1) {
      setCurrentStage(prev => prev + 1);
      setSelectedId(null);
      setSubmitted(false);
      setShowIntrusion(false);
    } else {
      setDone(true);
    }
  };

  if (!stage) return null;

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center h-full p-6 text-white"
      >
        <div className="bg-slate-800 rounded-xl border border-green-500/30 p-8 max-w-lg w-full">
          <div className="text-center mb-6">
            <div className="text-4xl mb-3">🏁</div>
            <h2 className="text-2xl font-bold text-green-400">Replay Complete</h2>
            <p className="text-slate-300 text-sm mt-2">
              You stepped through all five stages of the 2015 Ukraine attack chain.
            </p>
          </div>
          {!isReplay && (
            <div className="bg-slate-900 rounded-lg p-4 text-center mb-5">
              <div className="text-3xl font-bold text-amber-400">{totalScore} pts</div>
              <div className="text-slate-400 text-xs mt-1">Final Score</div>
            </div>
          )}
          <div className="space-y-1.5 mb-6">
            {stageResults.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-slate-300">
                  Stage {i + 1}: {r.phase}
                </span>
                {!isReplay && (
                  <span className={r.firstAttempt ? 'text-green-400' : 'text-amber-400'}>
                    +{r.score}{r.firstAttempt ? ' ★' : ''}
                  </span>
                )}
              </div>
            ))}
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              onScoreUpdate && onScoreUpdate(totalScore);
              onComplete && onComplete();
            }}
            className="w-full py-3 bg-green-600 hover:bg-green-500 rounded-lg font-semibold text-white"
          >
            Continue
          </motion.button>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="flex flex-col h-full text-white p-4 overflow-y-auto">
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-bold text-amber-400 tracking-wide uppercase">
          Ukraine Incident Replay
        </h2>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowHelp(true)}
            title="How to play"
            className="w-6 h-6 rounded-full border border-amber-700/60 bg-amber-950/60 text-amber-400 hover:text-amber-200 hover:border-amber-500 text-[12px] font-bold flex items-center justify-center transition-colors"
          >
            ?
          </button>
          {!isReplay && (
            <span className="text-sm text-slate-300">
              Score: <span className="text-amber-400 font-bold">{totalScore}</span>
            </span>
          )}
        </div>
      </div>

      <StageTimeline
        stages={stages}
        currentStage={currentStage}
        stageResults={stageResults}
      />

      <AnimatePresence mode="wait">
        <motion.div
          key={currentStage}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.25 }}
          className="flex-1 flex flex-col"
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-slate-400 uppercase tracking-wider">
              Stage {currentStage + 1}
            </span>
            <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">
              {stage.phase}
            </span>
          </div>

          <div
            className="rounded-lg p-4 mb-4"
            style={{
              background: 'linear-gradient(135deg, rgba(0,30,60,0.85) 0%, rgba(10,20,50,0.9) 100%)',
              border: '1px solid rgba(0,212,255,0.25)',
              borderLeft: '3px solid #00d4ff',
              boxShadow: '0 0 12px rgba(0,212,255,0.07) inset',
            }}
          >
            <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: '#00d4ff' }}>
              Situation
            </p>
            <p className="text-slate-200 text-sm leading-relaxed">{stage.attackerAction}</p>
          </div>

          <AnimatePresence>
            {showIntrusion && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-4 rounded-lg overflow-hidden border border-red-500/30"
              >
                <NetworkTopology
                  nodeStates={nodeStates}
                  intrusionPath={STAGE3_INTRUSION_PATH}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {!submitted && (
            <div className="flex flex-col gap-2">
              <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: '#00ff88' }}>
                Defender Response
              </p>
              {stage.options.map(opt => (
                <motion.button
                  key={opt.id}
                  whileHover={{ scale: 1.005 }}
                  whileTap={{ scale: 0.995 }}
                  onClick={() => setSelectedId(opt.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-all duration-150 ${
                    selectedId === opt.id
                      ? 'border-amber-500 text-white'
                      : 'text-slate-300 hover:border-slate-500'
                  }`}
                  style={
                    selectedId === opt.id
                      ? { background: 'rgba(180,100,0,0.18)', borderColor: '#f59e0b' }
                      : {
                          background: 'rgba(15,25,45,0.75)',
                          borderColor: 'rgba(0,255,136,0.15)',
                        }
                  }
                >
                  {opt.text}
                </motion.button>
              ))}
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={handleSubmit}
                disabled={!selectedId}
                className="mt-2 w-full py-2.5 bg-amber-600 hover:bg-amber-500 disabled:bg-slate-700 disabled:text-slate-500 rounded-lg font-semibold text-sm transition-colors"
              >
                Submit Response
              </motion.button>
            </div>
          )}

          <AnimatePresence>
            {submitted && selectedOpt && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className={`rounded-lg p-4 mb-4 border ${
                  selectedOpt.isCorrect
                    ? 'bg-green-900/30 border-green-500/40'
                    : 'bg-red-900/30 border-red-500/40'
                }`}
              >
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`text-sm font-bold ${selectedOpt.isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                    {selectedOpt.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                  </span>
                  {selectedOpt.isCorrect && isFirstAttempt && !isReplay && (
                    <span className="text-xs text-amber-400">
                      +{firstAttemptBonus} first-attempt bonus
                    </span>
                  )}
                </div>
                <p className="text-slate-200 text-sm leading-relaxed">{selectedOpt.feedback}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {submitted && (
            <div className="flex gap-2 mt-auto">
              {!selectedOpt?.isCorrect && (
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={handleRetry}
                  className="flex-1 py-2.5 bg-slate-700 hover:bg-slate-600 rounded-lg font-semibold text-sm text-slate-200"
                >
                  Try Again
                </motion.button>
              )}
              {selectedOpt?.isCorrect && (
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={handleNext}
                  className="flex-1 py-2.5 bg-green-600 hover:bg-green-500 rounded-lg font-semibold text-sm text-white"
                >
                  {currentStage < stages.length - 1 ? 'Next Stage →' : 'Complete Replay'}
                </motion.button>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default UkraineReplay;
